// TanStack Query hooks for the recordings endpoints. Bodies fully wired; the api stubs
// they call return data once implemented.

import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { apiFetch } from "./client";
import type {
  ActionResponse,
  BulkReviewRequest,
  BulkReviewResponse,
  FraudDecisionRequest,
  FraudStats,
  HandsData,
  RecordingDeleteResponse,
  RecordingDetail,
  RecordingList,
  RecordingListSummary,
  ReviewRequest,
  ScanRequest,
  ScanResponse,
} from "./types";

const PROCESSING_STATUSES = new Set([
  "discovered",
  "downloading",
  "validating",
  "processing",
]);

export interface RecordingsFilter {
  status?: string; // comma-separated
  org?: string;
  view?: string; // Recordings page pill, evaluated server-side (see RecordingListSummary.counts)
  uploader?: string; // customer uid, or "__unknown__"
  sort?: "newest" | "score";
  limit?: number;
  offset?: number;
}

function qs(filter: RecordingsFilter): string {
  const p = new URLSearchParams();
  if (filter.status) p.set("status", filter.status);
  if (filter.org) p.set("org", filter.org);
  if (filter.view) p.set("view", filter.view);
  if (filter.uploader) p.set("uploader", filter.uploader);
  if (filter.sort) p.set("sort", filter.sort);
  if (filter.limit != null) p.set("limit", String(filter.limit));
  if (filter.offset != null) p.set("offset", String(filter.offset));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function useRecordings(filter: RecordingsFilter = {}) {
  return useQuery({
    queryKey: ["recordings", filter],
    queryFn: () => apiFetch<RecordingList>(`/api/recordings${qs(filter)}`),
    placeholderData: keepPreviousData,
    refetchInterval: 5000, // queue auto-advances as the worker runs
  });
}

/** Pill counts + Name filter options for the Recordings page (independent of the page shown). */
export function useRecordingsSummary() {
  return useQuery({
    queryKey: ["recordings", "summary"],
    queryFn: () => apiFetch<RecordingListSummary>("/api/recordings/summary"),
    placeholderData: keepPreviousData,
    refetchInterval: 5000,
  });
}

export function useRecording(id: number | string) {
  return useQuery({
    queryKey: ["recording", String(id)],
    queryFn: () => apiFetch<RecordingDetail>(`/api/recordings/${id}`),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && PROCESSING_STATUSES.has(status) ? 3000 : false;
    },
  });
}

/** Per-frame hand keypoints for the video overlay. Fetched once, cached aggressively. */
export function useHands(url: string | null | undefined) {
  return useQuery({
    queryKey: ["hands", url],
    enabled: Boolean(url),
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    queryFn: () => apiFetch<HandsData>(url as string),
  });
}

function useReviewMutation(id: number | string, action: "approve" | "reject") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReviewRequest) =>
      apiFetch<ActionResponse>(`/api/recordings/${id}/${action}`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recording", String(id)] });
      qc.invalidateQueries({ queryKey: ["recordings"] });
    },
  });
}

export const useApprove = (id: number | string) => useReviewMutation(id, "approve");
export const useReject = (id: number | string) => useReviewMutation(id, "reject");

/** Batch approve/reject from the list pages — one request, partial success reported per id. */
export function useBulkReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkReviewRequest) =>
      apiFetch<BulkReviewResponse>(`/api/recordings/bulk-review`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (res) => {
      // the decided rows' detail queries are stale too
      for (const r of res.results) {
        if (!r.error) qc.invalidateQueries({ queryKey: ["recording", String(r.recording_id)] });
      }
      qc.invalidateQueries({ queryKey: ["recordings"] });
    },
  });
}

/** Hard delete (DB row + bucket objects).
 * The detail query is dropped, not invalidated — the row is gone and a refetch would 404. */
export function useDeleteRecording(id: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<RecordingDeleteResponse>(`/api/recordings/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ["recording", String(id)] });
      qc.invalidateQueries({ queryKey: ["recordings"] });
    },
  });
}

export function useReprocess(id: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<ActionResponse>(`/api/recordings/${id}/reprocess`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recording", String(id)] });
      qc.invalidateQueries({ queryKey: ["recordings"] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

// ── anti-fraud decisions (Fraud Detection page) ───────────────────────────────

function useFraudDecision(id: number | string, action: "confirm" | "clear") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FraudDecisionRequest = {}) =>
      apiFetch<ActionResponse>(`/api/recordings/${id}/fraud/${action}`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recording", String(id)] });
      qc.invalidateQueries({ queryKey: ["recordings"] });
      qc.invalidateQueries({ queryKey: ["fraud-stats"] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

/** Human confirms the anti_fraud verdict -> terminal `fraud`. */
export const useConfirmFraud = (id: number | string) => useFraudDecision(id, "confirm");
/** Human overrides it (not fraud) -> back through the pipeline, stage skipped. */
export const useClearFraud = (id: number | string) => useFraudDecision(id, "clear");

export function useFraudStats() {
  return useQuery({
    queryKey: ["fraud-stats"],
    queryFn: () => apiFetch<FraudStats>(`/api/recordings/fraud-summary`),
    refetchInterval: 15000,
  });
}

export function useScan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ScanRequest = {}) =>
      apiFetch<ScanResponse>(`/api/ingest/scan`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recordings"] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}
